// Charm rack + status (luck, pity) + offer yourself.
import { B } from '../../content/balance';
import { CHARMS, DEALS } from '../../content/registry';
import { t } from '../../i18n/t';
import { formatMult } from '../../i18n/format';
import type { MoodId } from '../../sim/types';
import { currentLuck, hungerOf, modsOf, type HungerView } from '../../state/selectors';
import { setUI, useGame, useUI } from '../../state/store';
import { controller } from '../../render';
import { columnDefs } from '../../sim/grid';
import { crystalBallStop } from '../../sim/spin';
import { Bar, Btn, Frame, Icon, KText } from '../common';
import { tip } from '../tooltip/Tooltip';

export function CharmTip({ id, asleep }: { id: string; asleep?: boolean }) {
  const cursed = CHARMS.get(id)?.cursed;
  return (
    <div className="col">
      <span className="h" style={{ color: cursed ? '#e83b3b' : '#fbb954' }}>
        {t(`charm.${id}.name`)}
      </span>
      <span className="dim">{t(`rarity.${CHARMS.get(id)?.rarity ?? 'C'}`)}</span>
      <KText text={t(`charm.${id}.desc`)} />
      {asleep && <span className="kw-bad">{t('ui.charm.asleep')}</span>}
    </div>
  );
}

export function DealTip({ id, noFine }: { id: string; noFine?: boolean }) {
  return (
    <div className="col">
      <span className="h kw-bad">{t(`deal.${id}.name`)}</span>
      <KText text={`${t('ui.deal.boon')}: ${t(`deal.${id}.boon`)}`} />
      {noFine ? <span className="dim">{t('ui.deal.noFine')}</span> : <span className="kw-bad">{`${t('ui.deal.fine')}: `}<KText text={t(`deal.${id}.fine`)} /></span>}
    </div>
  );
}

export function MoodTip({ mood, ravenousX }: { mood: MoodId; ravenousX: number }) {
  return (
    <div className="col">
      <span className="h kw-hunger">{t(`mood.${mood}`)}</span>
      <KText text={t(`mood.${mood}.desc`, { x: formatMult(ravenousX) })} />
    </div>
  );
}

/** Hunger bar + mood (docs/07 §1.3). The bar throbs at RAVENOUS and above. */
function HungerStatus({ h, ravenousX, calm }: { h: HungerView; ravenousX: number; calm: boolean }) {
  const hot = h.hunger >= B.MOOD_RAVENOUS && !calm;
  return (
    <Frame>
      <div className="col" data-anchor="hunger">
        <div className="row" {...tip(<KText text={t('ui.hunger.tip')} />)}>
          <span className="kw-hunger">{t('ui.status.hunger')}</span>
          <span className="h kw-hunger">{t('ui.status.hungerValue', { n: Math.floor(h.hunger) })}</span>
        </div>
        <div className={hot ? 'throb' : undefined}>
          <Bar value={h.hunger} max={B.HUNGER_MAX} color={h.hunger >= B.HUNGER_MAX ? 'red' : 'flesh'} />
        </div>
        <div className="row" {...tip(<MoodTip mood={h.mood} ravenousX={ravenousX} />)} data-testid="mood">
          <span>{t('ui.status.mood')}</span>
          <span className="h kw-hunger">{t(`mood.${h.mood}`)}</span>
        </div>
      </div>
    </Frame>
  );
}

/** Doom pips (docs/07 §1.3: DOOM ◉◉○○○○). */
function DoomStatus({ doom, max, calm }: { doom: number; max: number; calm: boolean }) {
  return (
    <Frame>
      <div className="row" data-anchor="doom" data-testid="doom" {...tip(<KText text={t('ui.doom.tip')} />)}>
        <span className="kw-doom">{t('ui.doom')}</span>
        <span className={`row ${doom >= max && !calm ? 'throb' : ''}`} style={{ gap: 2 }}>
          {Array.from({ length: max }, (_, i) => (
            <span key={i} className={`pip ${i < doom ? 'pip-on' : ''}`} />
          ))}
        </span>
      </div>
    </Frame>
  );
}

/** Crystal Ball: reel 1's next stop, before you pull. */
function CrystalBall() {
  const game = useGame((g) => g);
  const run = game.run!;
  const stop = crystalBallStop(run);
  if (stop === null) return null;
  const col = columnDefs(run, 0, stop, 0);
  return (
    <Frame title={t('ui.crystal')}>
      <div className="row" style={{ justifyContent: 'center', gap: 2 }}>
        {col.map((id, i) => (
          <Icon key={i} id={`sym_${id}`} scale={0.5} />
        ))}
      </div>
    </Frame>
  );
}

export function RightPanel() {
  const game = useGame((g) => g);
  const busy = useUI((u) => u.busy);
  const drawer = useUI((u) => u.drawer);
  const run = game.run;
  if (!run) return null;
  const mods = modsOf(game);
  const luck = currentLuck(game);
  const hunger = hungerOf(game);
  const { reels } = B.SIZES[run.size];
  const slots = mods.charmSlots;
  const fated = luck.total >= reels;
  return (
    <div className={`rightpanel interactive drawer drawer-right ${drawer === 'status' ? 'open' : ''}`}>
      {mods.features.has('shop') && (
        <Frame title={t('ui.charms.title')}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {Array.from({ length: Math.max(slots, run.charms.length, 2) }, (_, i) => {
              const id = run.charms[i];
              const asleep = i >= slots;
              return (
                <div
                  key={i}
                  className={`paper ${asleep ? 'asleep' : ''}`}
                  draggable={!!id}
                  onDragStart={(e) => e.dataTransfer.setData('text/charm', String(i))}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    const from = Number(e.dataTransfer.getData('text/charm'));
                    if (Number.isNaN(from) || from === i || !run.charms[from]) return;
                    const order = [...run.charms];
                    const [moved] = order.splice(from, 1);
                    order.splice(Math.min(i, order.length), 0, moved!);
                    controller.act({ t: 'REORDER_CHARMS', order });
                  }}
                  style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', background: id ? undefined : '#45293f', cursor: id ? 'grab' : undefined }}
                  {...(id ? tip(<CharmTip id={id} asleep={asleep} />) : tip(<span>{t('ui.charms.empty')}</span>))}
                >
                  {id && <Icon id={`charm_${id}`} />}
                </div>
              );
            })}
          </div>
        </Frame>
      )}
      {mods.crystalBall && <CrystalBall />}
      {run.deals.length > 0 && (
        <Frame title={t('ui.deal.active')}>
          <div className="col" style={{ gap: 1 }}>
            {run.deals.map((id) => (
              <div key={id} className="kw-bad" {...tip(<DealTip id={id} noFine={!!run.flags.covenant && !DEALS.get(id)?.covenant} />)}>
                {t(`deal.${id}.name`)}
              </div>
            ))}
          </div>
        </Frame>
      )}
      <Frame>
        <div className="col">
          <div
            className="row"
            {...tip(
              <div className="col">
                <KText text={t('ui.odds.luck', { n: luck.total })} />
                <KText text={t('ui.odds.pity', { n: mods.pityAfter, luck: B.PITY_BASE_LUCK, step: B.PITY_STEP })} />
              </div>,
            )}
          >
            <span className="kw-luck">{t('ui.status.luck')}</span>
            <span className="h kw-luck">
              {luck.total}
              {fated ? ` ${t('ui.status.fated')}` : ''}
            </span>
          </div>
          {luck.pity > 0 && <div className="kw-luck">{t('ui.status.pity', { n: luck.pity })}</div>}
        </div>
      </Frame>
      {hunger && <HungerStatus h={hunger} ravenousX={mods.ravenousX} calm={game.meta.settings.reduceFlashing} />}
      {mods.features.has('eyes') && <DoomStatus doom={run.doom} max={mods.doomMax} calm={game.meta.settings.reduceFlashing} />}
      {!!run.flags.endless && <div className="h kw-bad" style={{ textAlign: 'center' }}>{t('ui.endless.badge')}</div>}
      <span style={{ flex: 1 }} />
      <Btn kind="danger" disabled={busy || run.phase === 'consumed' || run.phase === 'bonus'} onClick={() => setUI({ confirmOffer: true })} testId="offer-self">
        {t('ui.offerSelf')}
      </Btn>
    </div>
  );
}
